import {
  Flex,
  IconButton,
  Input,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as lodash from 'lodash';
import React, { useCallback, useState } from 'react';

import { validate as athenaValidate } from '@finley-technologies/athena-utilities';
import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  DataLake,
  typeguards,
  Util,
} from '@finley-technologies/finley-types';

import { ReportingOutputDocxColumnConfigContainer } from './ReportingOutputDocxColumnConfigContainer';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useGetAthenaQueryResponseByRawLazyQueryGQLType } from '~/packages/athena/requests/requests.gql.generated';
import { AthenaDataPreviewModal } from '~/packages/athena/ui/AthenaDataPreviewModal';
import { AthenaQueryExecuteModal } from '~/packages/athena/ui/AthenaQueryExecuteModal';
import { AthenaQueryStringWithSchemaSidebarContainer } from '~/packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import { BindingContainer } from '~/packages/athena/ui/BindingContainer';
import { ColumnNamesContainer } from '~/packages/athena/ui/ColumnNamesContainer';
import { inputWidth } from '~/packages/athena/ui/consts';
import { VariableContainer } from '~/packages/athena/ui/VariableContainer';
import {
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  SearchIconButton,
} from '~/packages/common-frontend/components/IconButton';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { DeleteConfirmationModal } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { CreditAgreementFullArtifactWipConfigType } from '~/packages/creditAgreements/ui/types';

type ReportingOutputDocxFileInsertionAthenaQueryOutputProps = {
  organizationId: string;
  fileInsertionOutputName: string;
  athenaQueryOutputConfig: CreditAgreement.Config.Reporting.Outputs.Docx.AthenaQueryFileInsertionConfig;
  existingFileInsertionOutputs: CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionOutput[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  basePropertyKey: string;
  idx: number;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
};
export const ReportingOutputDocxFileInsertionAthenaQueryOutput = ({
  organizationId,
  fileInsertionOutputName,
  athenaQueryOutputConfig,
  existingFileInsertionOutputs,
  wipConfig,
  fullPropertyKey,
  basePropertyKey,
  idx,
  presetVariables,
  validationVariables,
}: ReportingOutputDocxFileInsertionAthenaQueryOutputProps) => {
  const toast = useToast();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();
  const { onOpen: onPreviewQueryResultsDrawerOpen } = useDisclosure();
  const queryStringEditModeDisclosure = useDisclosure();

  const { athenaQuery: queryView } = athenaQueryOutputConfig;

  const inputDateType = 'reportingDate';
  const queryPropertyKey = `${fullPropertyKey}.config.athenaQuery`;

  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  const [isExecuteModalOpen, setIsExecuteModalOpen] = useState<boolean>(false);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);
  const [
    isQueryStringSaveLoading,
    setIsQueryStringSaveLoading,
  ] = useState<boolean>(false);

  const [queryStringState, setQueryStringState] = useState<string>(
    queryView.query.queryString,
  );
  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[]
  >([]);

  const deleteQuery = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const fileInsertionConfigs: CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionOutput[] = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );

    finleyAssert(
      typeguards.creditAgreement.isDocxFileInsertionOutputs(
        fileInsertionConfigs,
      ),
      `Retrieved non-DocxFileInsertionOutputs structure for [key:${basePropertyKey}]`,
    );

    const configsAfterDelete = fileInsertionConfigs.filter(
      (_unused, varIdx) => varIdx !== idx,
    );

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: configsAfterDelete,
      setIsSaveLoading: setIsDeleteLoading,
    });
  }, [wipConfig, basePropertyKey, idx]);

  const [
    isFileOutputNameEditOpen,
    setFileOutputNameEditOpen,
  ] = useState<boolean>(false);
  const [fileOutputName, setFileOutputName] = useState<string>(
    fileInsertionOutputName,
  );

  const changeFileOutputName = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const nameExists = existingFileInsertionOutputs.some(
      (output) => output.name === fileOutputName,
    );

    if (nameExists) {
      toast({
        ...errorToastConfig,
        title: 'Updating Output Variable Name Error',
        description: `"${fileOutputName}" name is already in use`,
      });
      return;
    }

    const fileOutput = getIn(wipConfig.formProps.values, fullPropertyKey);
    fileOutput.name = fileOutputName;
    wipConfig.formProps.setFieldValue(fullPropertyKey, fileOutput);
    setFileOutputNameEditOpen(false);
  }, [
    existingFileInsertionOutputs,
    fileOutputName,
    fullPropertyKey,
    setFileOutputNameEditOpen,
    wipConfig,
    toast,
  ]);

  const [
    executeAthenaQueryByRaw,
    { loading: isQueryLoading, variables: queryVariables },
  ] = useGetAthenaQueryResponseByRawLazyQueryGQLType({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: async (data) => {
      const { Items } = data.athenaQueryResponse;

      if (Items.length === 0 || Object.keys(Items[0]).length === 0) {
        toast({
          title: 'Athena Query Execute Update',
          description:
            'Query returned 0 data points. Query SQL and available column names have not been updated. Choose a different Reporting Date where data exists for the query and try again.',
          status: 'info',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });

        setIsQueryStringSaveLoading(false);
        return;
      }

      if (wipConfig?.isMutateAllowed(queryPropertyKey)) {
        const queryCopy = lodash.cloneDeep(queryView);

        // save column names and query string in CA
        queryCopy.columnNames = Object.keys(Items[0]);
        if (queryVariables) {
          // save if the queryString has changed
          // so that we do not unnecessarily save when just previewing
          if (queryCopy.query.queryString !== queryVariables.query) {
            queryCopy.query.queryString = queryVariables.query;

            await wipConfig?.saveAndUpdateConfigCreditAgreementWip({
              wipConfig,
              propertyKey: queryPropertyKey,
              updatedProperty: queryCopy,
              setIsSaveLoading: setIsQueryStringSaveLoading,
            });
          }
        }

        setIsQueryStringSaveLoading(false);
        // set the query preview results and open preview drawer
        setQueryDataItems(Items);
        onPreviewQueryResultsDrawerOpen();
      }
    },
    onError: (error) => {
      setIsQueryStringSaveLoading(false);

      const errorDescription = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Incurred server error when executing Athena query'
        : error.message;
      toast({
        ...errorToastConfig,
        title: 'Athena Query Execute Error',
        description: errorDescription,
      });
    },
  });

  const changeQueryString = useCallback(
    async ({
      newQueryString,
      queryInputDateForColumnUpdate,
    }: {
      newQueryString: string;
      queryInputDateForColumnUpdate: Util.FullDateStr;
    }): Promise<void> => {
      setIsQueryStringSaveLoading(true);

      if (!wipConfig?.isMutateAllowed(queryPropertyKey)) {
        return;
      }

      const qv = lodash.cloneDeep(
        getIn(wipConfig.formProps.values, queryPropertyKey),
      );
      qv.query.queryString = newQueryString;

      const errors = athenaValidate.validateAthenaQuery({
        query: qv.query.queryString,
        bindings: qv.bindings,
        variables: [
          ...validationVariables,
          ...presetVariables,
          ...qv.variables,
        ],
      });

      if (errors.length) {
        toast({
          ...errorToastConfig,
          title: 'Updating SQL Query Error',
          description: errors.join(', '),
        });
        setIsQueryStringSaveLoading(false);
        return;
      }

      const { variables, bindings } = queryView;

      const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
        {
          name: inputDateType,
          value: queryInputDateForColumnUpdate,
          parseNumber: true,
        },
      ];

      // If there's any overlap with provided "presetVariables", prioritize the
      // value that's set up with the modal through "supplementalVariables" and
      // filter out what already existed in "presetVariables"
      const updatedPresetVariables = presetVariables.filter((preset) => {
        return preset.name !== inputDateType;
      });

      executeAthenaQueryByRaw({
        variables: {
          organizationId,
          variables: [
            ...supplementalVariables,
            ...updatedPresetVariables,
            ...variables,
          ],
          bindings,
          query: newQueryString,
        },
      });
    },
    [
      organizationId,
      wipConfig,
      presetVariables,
      queryPropertyKey,
      toast,
      validationVariables,
      executeAthenaQueryByRaw,
      inputDateType,
      queryView,
    ],
  );

  return (
    <Flex mb="2" p="3" flexDir="column" border="1px" borderColor="gray.700">
      <Text fontWeight="bold">Athena Query</Text>
      <Flex alignItems="center" mb={isQueryOpen ? '2' : undefined}>
        <Text mr="2">Output Variable Name: &quot;{fileOutputName}&quot;</Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <SearchIconButton
              ariaLabel="execute query"
              label="Execute Query"
              onClick={() => setIsExecuteModalOpen(true)}
              isLoading={isQueryLoading}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <IconButton
              aria-label={`${isQueryOpen ? 'close' : 'display'} config`}
              icon={isQueryOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setIsQueryOpen((prev) => !prev)}
              variant="ghost"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>

      {isQueryOpen ? (
        <>
          {/* *** File Insertion Output Name Management *** */}
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="4"
          >
            <Text width="60">Output Variable Name:</Text>
            {isFileOutputNameEditOpen &&
            wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <>
                <Input
                  type="text"
                  width={inputWidth}
                  value={fileOutputName}
                  onChange={(e) => setFileOutputName(e.target.value)}
                />
                <SaveIconButton
                  ariaLabel="save name"
                  label="Save Name"
                  onClick={() => changeFileOutputName()}
                />
                <EditIconButton
                  ariaLabel="close edit"
                  label="Close Edit"
                  onClick={() => {
                    setFileOutputName(fileOutputName);
                    setFileOutputNameEditOpen(false);
                  }}
                />
              </>
            ) : (
              <>
                <Text width={inputWidth}>{fileOutputName}</Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <EditIconButton
                    ariaLabel="edit name"
                    label="Edit Name"
                    onClick={() => setFileOutputNameEditOpen(true)}
                    h="6"
                  />
                )}
              </>
            )}
          </Flex>
          {/* *** Variables Management *** */}
          <VariableContainer
            wipConfig={wipConfig}
            variables={queryView.variables}
            variablePropertyKey={`${queryPropertyKey}.variables`}
          />
          {/* *** Bindings Management *** */}
          <BindingContainer
            wipConfig={wipConfig}
            bindings={queryView.bindings}
            bindingsPropertyKey={`${queryPropertyKey}.bindings`}
            presetVariables={presetVariables}
            validationVariables={validationVariables}
            query={queryView}
            queryType="singleQuery"
          />
          {/* *** SQL Query Management *** */}
          <AthenaQueryStringWithSchemaSidebarContainer
            wipConfig={wipConfig}
            fullPropertyKey={`${queryPropertyKey}.queryString`}
            changeQueryString={changeQueryString}
            isQueryStringSaveLoading={isQueryStringSaveLoading}
            queryStringState={queryStringState}
            setQueryStringState={setQueryStringState}
            initialQueryString={queryView.query.queryString}
            editModeDisclosure={queryStringEditModeDisclosure}
          />
          {/* *** Column Name Portion *** */}
          <ColumnNamesContainer
            wipConfig={wipConfig}
            columnNames={queryView.columnNames}
            configPropertyKey={queryPropertyKey}
            executeQueryMethod={() => setIsExecuteModalOpen(true)}
          />
          <ReportingOutputDocxColumnConfigContainer
            columnConfigs={athenaQueryOutputConfig.columns}
            wipConfig={wipConfig}
            basePropertyKey={`${fullPropertyKey}.config.columns`}
            columnNames={queryView.columnNames}
          />
        </>
      ) : null}

      {wipConfig?.isMutateAllowed(queryPropertyKey) && (
        <AthenaQueryExecuteModal
          organizationId={organizationId}
          wipConfig={wipConfig}
          athenaQuery={queryView}
          fullPropertyKey={queryPropertyKey}
          inputDateType="reportingDate"
          presetVariables={presetVariables}
          isOpen={isExecuteModalOpen}
          onCancel={() => setIsExecuteModalOpen(false)}
          onQuerySuccess={setQueryDataItems}
        />
      )}

      <AthenaDataPreviewModal
        queryDataItems={queryDataItems}
        onClose={() => setQueryDataItems([])}
      />
    </Flex>
  );
};
